import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import Iconify from "../../components/iconify";
import { useSettingsContext } from "../../components/settings";
import logo from "../../assets/logo.png";

// ----------------------------------------------------------------------

const driftA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(6%, 8%) scale(1.15); }
`;
const driftB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-8%, -6%) scale(1.2); }
`;
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const reduceMotion = { "@media (prefers-reduced-motion: reduce)": { animation: "none !important" } };

const capabilities = [
  { icon: "eva:shopping-cart-outline", label: "Take orders at the POS terminal" },
  { icon: "eva:grid-outline", label: "Manage tables and live orders" },
  { icon: "eva:book-open-outline", label: "Keep your menu up to date" },
  { icon: "eva:cube-outline", label: "Track inventory and suppliers" },
  { icon: "eva:people-outline", label: "Manage staff and customers" },
  { icon: "eva:pricetags-outline", label: "Run promotions and offers" },
  { icon: "eva:monitor-outline", label: "Send orders to the kitchen display" },
  { icon: "eva:bar-chart-2-outline", label: "Follow sales with reports" },
];

const defaultPanel = {
  eyebrow: "Branch Portal",
  title: "Run your branch from one screen.",
  lead: "Everything your team needs, from the first order to the final report.",
  items: capabilities,
};

AuthLayout.propTypes = {
  children: PropTypes.node,
  maxWidth: PropTypes.number,
  panel: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    lead: PropTypes.string,
    items: PropTypes.array,
  }),
};

function BrandPanel({ panel }) {
  return (
    <Box
      sx={(theme) => ({
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        gap: 4,
        position: "relative",
        overflow: "hidden",
        width: { md: "46%", lg: "50%" },
        flexShrink: 0,
        color: "common.white",
        px: { md: 6, lg: 9 },
        py: 6,
        bgcolor: theme.palette.grey[900],
        borderInlineEnd: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
      })}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          position: "absolute",
          insetInlineStart: "-12%",
          top: "-14%",
          width: 560,
          height: 560,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.5)} 0%, transparent 68%)`,
          filter: "blur(30px)",
          animation: `${driftA} 18s ease-in-out infinite`,
          ...reduceMotion,
        })}
      />
      <Box
        aria-hidden
        sx={(theme) => ({
          position: "absolute",
          insetInlineEnd: "-18%",
          bottom: "-16%",
          width: 620,
          height: 620,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.34)} 0%, transparent 68%)`,
          filter: "blur(40px)",
          animation: `${driftB} 24s ease-in-out infinite`,
          ...reduceMotion,
        })}
      />
      <Box
        aria-hidden
        sx={(theme) => ({
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(to right, ${alpha(theme.palette.common.white, 0.05)} 1px, transparent 1px), linear-gradient(to bottom, ${alpha(theme.palette.common.white, 0.05)} 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
          WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 30% 35%, #000 25%, transparent 78%)",
          maskImage: "radial-gradient(ellipse 80% 70% at 30% 35%, #000 25%, transparent 78%)",
        })}
      />

      <Box sx={{ position: "relative" }}>
        <Box component="img" src={logo} alt="Egora" sx={{ height: 40, width: "auto", filter: "brightness(0) invert(1)" }} />
      </Box>

      <Stack spacing={4.5} sx={{ position: "relative", maxWidth: 540, my: "auto", "@media (max-height: 820px)": { "& .lead": { display: "none" } }, animation: `${riseIn} 0.7s ease both`, ...reduceMotion }}>
        <Box>
          <Typography variant="overline" sx={{ color: "primary.main", letterSpacing: 1.5 }}>
            {panel.eyebrow}
          </Typography>
          <Typography variant="h2" sx={{ mt: 1.5, color: "common.white", lineHeight: 1.15, fontSize: { md: 36, lg: 46 } }}>
            {panel.title}
          </Typography>
          <Typography className="lead" sx={{ mt: 2.5, color: alpha("#fff", 0.68), fontSize: 17, lineHeight: 1.7 }}>
            {panel.lead}
          </Typography>
        </Box>

        {panel.items && (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gridTemplateColumns: { md: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
          {panel.items.map((c) => (
            <Stack key={c.label} component="li" direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={(theme) => ({
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  color: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                })}
              >
                <Iconify icon={c.icon} width={18} />
              </Box>
              <Typography variant="body2" sx={{ color: alpha("#fff", 0.85), fontWeight: 500 }}>
                {c.label}
              </Typography>
            </Stack>
          ))}
        </Box>
        )}
      </Stack>

    </Box>
  );
}

export default function AuthLayout({ children, panel = defaultPanel, maxWidth = 440 }) {
  const { themeMode, onToggleMode } = useSettingsContext();

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <BrandPanel panel={panel} />

      <Box
        sx={(theme) => ({
          position: "relative",
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundImage: `radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.1 : 0.12)}, transparent 42%)`,
        })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 2.5, md: 4 }, py: 2.5 }}>
          <Box
            component="img"
            src={logo}
            alt="Egora"
            sx={(theme) => ({
              display: { xs: "block", md: "none" },
              height: 32,
              width: "auto",
              filter: theme.palette.mode === "dark" ? "brightness(0) invert(1)" : "none",
            })}
          />
          <Box sx={{ display: { xs: "none", md: "block" } }} />
          <Tooltip title={themeMode === "light" ? "Switch to dark mode" : "Switch to light mode"}>
            <IconButton onClick={onToggleMode} aria-label="Toggle theme mode" sx={{ color: "text.secondary" }}>
              <Iconify icon={themeMode === "light" ? "eva:moon-outline" : "eva:sun-outline"} width={22} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2.5, sm: 4 }, pb: 4 }}>
          <Box sx={{ width: 1, maxWidth, animation: `${riseIn} 0.6s ease both`, ...reduceMotion }}>{children}</Box>
        </Box>

        <Typography variant="caption" align="center" sx={{ color: "text.disabled", pb: 3 }}>
          &copy; {new Date().getFullYear()} Egora. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
